/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { Link } from "../components/ui/Link";
import { ChevronRight, Copy, RefreshCw, Search } from "lucide-react";
import { listOpenCases, triggerScan, SIGNALS, type AccountSide, type DuplicateCase } from "../api/duplicates";
import { Button } from "../components/ui/Button";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { InfiniteList } from "../components/ui/InfiniteList";
import { Input } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { debounce } from "../lib/utils";
import { ScoreBadge } from "../components/duplicates/ScoreBadge";
import { useChrome } from "../contexts/ChromeContext";
import { PAGE_SIZE } from "../api/registry";
import { EmptyState } from "../components/ui/EmptyState";
import { useToast } from "../contexts/ToastContext";
import { fullName } from "../lib/accounts";
import { DUPLICATE_SORTS, paramsWithSort, sortChoice } from "../lib/sorting";
import { Select } from "../components/ui/Select";


/**
 * The duplicate review queue: every open case is a suspected pair the scan surfaced. Dismissing
 * silences the pair forever; confirming runs the merge. Detection is deterministic, so an empty
 * queue genuinely means no known signals fire.
 */
export function Duplicates() {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = sortChoice(DUPLICATE_SORTS, searchParams.get("sort") ?? "");
  const q = searchParams.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const [items, setItems] = useState<DuplicateCase[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  // one debounce for the component's lifetime; recreating it per render would drop pending calls
  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchParams(value.trim() === "" ? {} : { q: value.trim() }, { replace: true });
      }, 300),
    [setSearchParams],
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const requestSeq = useRef(0);
  const loadPage = (pageToLoad: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    void listOpenCases(PAGE_SIZE, (pageToLoad - 1) * PAGE_SIZE, q, sort).then((response) => {
      if (seq !== requestSeq.current) {
        return;
      }
      setLoading(false);
      if (!response.ok) {
        setError(response.error.message);
        return;
      }
      setError(null);
      setTotal(response.value.total);
      setPage(pageToLoad);
      if (pageToLoad === 1) {
        setItems(response.value.items);
        return;
      }
      setItems((previous) => {
        const seen = new Set((previous ?? []).map((item) => item.id));
        return [...(previous ?? []), ...response.value.items.filter((item) => !seen.has(item.id))];
      });
    });
  };
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  useEffect(() => {
    loadPageRef.current(1);
  }, [q, sort]);

  useChrome({ title: "Duplicates" }, []);

  async function scanNow() {
    setScanning(true);
    const response = await triggerScan();
    setScanning(false);
    if (response.ok) {
      showToast(
        "success",
        "Scan queued. New cases appear within a few minutes; refresh to see them.",
      );
    } else {
      showToast("error", response.error.message);
    }
  }

  if (error !== null && items === null) {
    return <ErrorMessage message={error} className="mt-8" />;
  }
  if (items === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const hasMore = items.length < total;

  return (
    <div className="space-y-8">
      <header className="enter flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
            Duplicates
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
            Suspected duplicate client accounts. Confirming merges the pair; dismissing silences
            it for good.
          </p>
        </div>
        <Button variant="secondary" size="md" disabled={scanning} onClick={() => void scanNow()}>
          {scanning ? <Spinner size="xs" className="mr-2" /> : <RefreshCw size={15} />}
          Scan now
        </Button>
      </header>

      <div className="space-y-6">
        <Input
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            applySearch(event.target.value);
          }}
          placeholder="Search by name, email, phone, account id, or Healthie id"
          aria-label="Search suspected duplicates"
          prefix={<Search size={15} className="text-text-tertiary" />}
          className="sm:max-w-xl"
        />
        <Select
          className="w-full sm:w-44"
          value={sort.value}
          options={DUPLICATE_SORTS}
          onChange={(value) =>
            setSearchParams((previous) => paramsWithSort(previous, value), { replace: true })
          }
          ariaLabel="Sort duplicates"
        />
        <p className="text-sm text-text-tertiary" aria-live="polite">
          {total === 1 ? "1 suspected duplicate" : `${total} suspected duplicates`}
          {loading && <Spinner size="xs" className="ml-2 inline-block align-middle" />}
        </p>
        <InfiniteList
          isLoading={loading}
          hasMore={hasMore}
          onLoadMore={() => loadPageRef.current(page + 1)}
          isEmpty={items.length === 0}
          currentPage={page}
          error={error}
          onRetry={() => loadPageRef.current(page + 1)}
          emptyState={
            <EmptyState
              icon={<Copy size={20} />}
              title="No suspected duplicates"
              description="The scan runs every six hours."
            />
          }
        >
          <ol className="grid gap-4 lg:grid-cols-2">
            {items.map((item, index) => (
              <CaseCard key={item.id} item={item} index={index} />
            ))}
          </ol>
        </InfiniteList>
      </div>
    </div>
  );
}

function sideLine(side: AccountSide): string {
  return side.email ?? side.username ?? side.phone ?? `Account ${side.id}`;
}

function CaseCard({ item, index }: { item: DuplicateCase; index: number }) {
  return (
    <li
      className="group border border-border bg-bg-elevated shadow-card lift enter relative flex h-full flex-col gap-3 rounded-3xl p-5"
      style={{ "--enter-delay": `${Math.min(index, 8) * 55 + 70}ms` } as CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-lg leading-tight text-text-primary">
            <Link
              to={`/care/duplicates/${item.id}`}
              className="after:absolute after:inset-0 after:rounded-3xl"
            >
              {fullName(item.accountA.firstName, item.accountA.lastName)}
              <span className="text-text-tertiary"> · </span>
              {fullName(item.accountB.firstName, item.accountB.lastName)}
            </Link>
          </h2>
          <p className="mt-0.5 truncate text-xs text-text-tertiary">
            {sideLine(item.accountA)} · {sideLine(item.accountB)}
          </p>
        </div>
        <ScoreBadge score={item.score} signals={item.signals} />
      </div>

      <ul className="space-y-1 text-sm text-text-secondary">
        {item.signals.map((signal) => (
          <li key={signal}>{SIGNALS[signal].label}</li>
        ))}
      </ul>

      <div className="mt-auto flex items-center justify-end border-t border-border/60 pt-3 text-xs">
        <span className="inline-flex items-center gap-1 font-medium text-text-tertiary transition-colors group-hover:text-text-primary">
          Review
          <ChevronRight
            size={14}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </span>
      </div>
    </li>
  );
}
